<template>
  <div class="rapport-activite position-relative">
    <div v-if="isLoading" class="rapport-activite-loading">
      <SpinnerLoading />
    </div>

    <div v-if="infoBanniere" class="alert alert-info small mb-2">{{ infoBanniere }}</div>
    <div v-if="fetchError" class="alert alert-warning small mb-2">{{ fetchError }}</div>

    <div class="d-flex justify-content-end mb-2">
      <button
        type="button"
        class="btn btn-primary btn-sm"
        :disabled="!canExport || isLoading || isExporting"
        @click="exportPdf"
      >
        <i class="fas fa-file-export mr-1"></i>
        {{ isExporting ? "Impression en cours…" : "Imprimer" }}
      </button>
    </div>

    <div ref="rapportExportRoot" class="rapport-export-root">
    <div class="rapport-page border" :class="{ 'rapport-page--dim': isLoading }">
      <header class="rapport-head d-flex flex-wrap align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <img v-if="logoSrc" :src="logoSrc" alt="" class="rapport-logo" />
          <div v-else class="rapport-logo-ph">
            <i class="fa-solid fa-mortar-pestle"></i>
          </div>
          <span class="rapport-pharma text-uppercase font-weight-bold ml-2">{{ nomPharmacie }}</span>
        </div>
        <div class="small text-right">Le {{ dateGenerationLabel }}</div>
      </header>

      <div class="rapport-title-frame text-center text-uppercase my-2">
        RAPPORT D'ACTIVITE DU {{ periodeDuLabel }} AU {{ periodeAuLabel }}
      </div>

      <!-- VENTES -->
      <section class="mb-3">
        <h2 class="section-title">VENTES</h2>
        <div class="table-responsive">
          <table class="table table-sm table-bordered mb-1 rapport-table rapport-table-ventes">
            <thead>
              <tr>
                <th style="width: 28%"></th>
                <th class="text-center">Ventes directes Espèces+Chèques</th>
                <th class="text-center">Ventes à crédit + Carte</th>
                <th class="text-center">TOTAL VENTES</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="font-weight-bold">Nombre de tickets</td>
                <td class="text-right">{{ fmtInt(ventes.directes.tickets) }}</td>
                <td class="text-right">{{ fmtInt(ventes.credit.tickets) }}</td>
                <td class="text-right font-weight-bold">{{ fmtInt(ventes.total.tickets) }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">C.A Brut</td>
                <td class="text-right">{{ fmtMoney(ventes.directes.ca_brut) }}</td>
                <td class="text-right">{{ fmtMoney(ventes.credit.ca_brut) }}</td>
                <td class="text-right font-weight-bold">{{ fmtMoney(ventes.total.ca_brut) }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Remise</td>
                <td class="text-right">{{ fmtMoney(ventes.directes.remise) }}</td>
                <td class="text-right">{{ fmtMoney(ventes.credit.remise) }}</td>
                <td class="text-right">{{ fmtMoney(ventes.total.remise) }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">C.A Net</td>
                <td class="text-right">{{ fmtMoney(ventes.directes.ca_net) }}</td>
                <td class="text-right">{{ fmtMoney(ventes.credit.ca_net) }}</td>
                <td class="text-right font-weight-bold">{{ fmtMoney(ventes.total.ca_net) }}</td>
              </tr>
              <tr class="rapport-ticket-moyen-row">
                <td colspan="2" class="text-center font-weight-bold">Ticket Moyen</td>
                <td colspan="2" class="text-center font-weight-bold">{{ fmtMoney(ventes.ticket_moyen) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Règlements -->
      <section class="mb-3 rapport-reglements">
        <div class="font-weight-bold">Règlements Clients (Espèces + Chèques) : <span style="display:inline-block;width:30px;"></span>{{ fmtMoney(reglements.clients) }}</div>
        <div class="font-weight-bold">Règlements Divers (Espèces + Chèques) : <span style="display:inline-block;width:30px;"></span>{{ fmtMoney(reglements.divers) }}</div>
      </section>

      <!-- ACHATS -->
      <section class="mb-3">
        <div class="table-responsive">
          <table class="table table-sm table-bordered mb-0 rapport-table rapport-table-achats">
            <thead>
              <tr>
                <th rowspan="2" class="align-middle">ACHATS</th>
                <th colspan="2" class="text-center">Bons de livraison ou Avoirs Validés</th>
                <th colspan="2" class="text-center">Bons de livraison ou Avoirs datés par fournisseurs</th>
              </tr>
              <tr>
                <th class="text-center">Bons de livraison</th>
                <th class="text-center">Avoirs</th>
                <th class="text-center">Bons de livraison</th>
                <th class="text-center">Avoirs</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Nombre de documents</td>
                <td class="text-right">{{ fmtInt(achats.valides.bl.nb) }}</td>
                <td class="text-right">{{ fmtInt(achats.valides.avoirs.nb) }}</td>
                <td class="text-right">{{ fmtInt(achats.fournisseurs.bl.nb) }}</td>
                <td class="text-right">{{ fmtInt(achats.fournisseurs.avoirs.nb) }}</td>
              </tr>
              <tr>
                <td>P.P.V</td>
                <td class="text-right">{{ fmtMoney(achats.valides.bl.ppv) }}</td>
                <td class="text-right">{{ fmtMoney(achats.valides.avoirs.ppv) }}</td>
                <td class="text-right">{{ fmtMoney(achats.fournisseurs.bl.ppv) }}</td>
                <td class="text-right">{{ fmtMoney(achats.fournisseurs.avoirs.ppv) }}</td>
              </tr>
              <tr>
                <td>P.P.H</td>
                <td class="text-right">{{ fmtMoney(achats.valides.bl.pph) }}</td>
                <td class="text-right">{{ fmtMoney(achats.valides.avoirs.pph) }}</td>
                <td class="text-right">{{ fmtMoney(achats.fournisseurs.bl.pph) }}</td>
                <td class="text-right">{{ fmtMoney(achats.fournisseurs.avoirs.pph) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ECHANGES -->
      <section class="mb-3">
        <div class="table-responsive">
          <table class="table table-sm table-bordered mb-0 rapport-table rapport-table-echanges">
            <thead>
              <tr>
                <th style="width: 40%">ECHANGES EN P.P.M</th>
                <th class="text-center">Entrée</th>
                <th class="text-center">Sorties</th>
                <th class="text-center">SOLDE=Entrée-Sortie</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Nombre d'échanges</td>
                <td class="text-right">{{ fmtInt(echanges.entree.nb) }}</td>
                <td class="text-right">{{ fmtInt(echanges.sorties.nb) }}</td>
                <td class="text-right">{{ fmtInt(echanges.solde.nb) }}</td>
              </tr>
              <tr>
                <td>P.P.V</td>
                <td class="text-right">{{ fmtMoney(echanges.entree.ppv) }}</td>
                <td class="text-right">{{ fmtMoney(echanges.sorties.ppv) }}</td>
                <td class="text-right">{{ fmtMoney(echanges.solde.ppv) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- RECAP PAR TYPE DU STOCK ÉCOULÉ -->
      <section>
        <div class="table-responsive">
          <table class="table table-sm table-bordered mb-0 rapport-table rapport-table-recap">
            <thead>
              <tr>
                <th>RECAPITULATIF PAR TYPE DU STOCK ÉCOULÉ</th>
                <th class="text-right">Taux de Marge</th>
                <th class="text-right">Prix Public Ventes</th>
                <th class="text-right">Prix Public BL et Avoirs</th>
                <th class="text-right">P.P.H. BL et Avoirs</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in recapLignes" :key="recapPeriodKey + '-' + i + '-' + row.type_actuel">
                <td>{{ row.type_actuel }}</td>
                <td class="text-right">{{ fmtRecapTaux(row.taux_marge) }}</td>
                <td class="text-right">{{ fmtMoney(row.prix_public_ttc) }}</td>
                <td class="text-right"></td>
                <td class="text-right"></td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="rapport-totaux-row">
                <td class="font-weight-bold">TOTAUX</td>
                <td class="text-right">—</td>
                <td class="text-right font-weight-bold">{{ fmtMoney(recapTotaux.prix_public_ttc) }}</td>
                <td class="text-right font-weight-bold">{{ fmtMoney(recapTotaux.pp_bl_avoirs) }}</td>
                <td class="text-right font-weight-bold">{{ fmtMoney(recapTotaux.pph_bl_avoirs) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </div>
    </div>
  </div>
</template>

<script>
import SpinnerLoading from "@/components/SpinnerLoading.vue";
import logoClementine from "@/assets/logo_clementine.png";
import {
  computeRecapActiviteTotaux,
  extractLignesBrutes,
  lignesBrutesToRecapActivite,
  normalizeTypePeriode,
  normalizeRecapType,
  overrideMargeParType,
} from "@/utils/rapportStockHelper";

function emptyBloc() {
  return { nb: null, ppv: null, pph: null };
}

function emptyVenteCol() {
  return { tickets: null, ca_brut: null, remise: null, ca_net: null };
}

/**
 * GET /api/statistiques/rapport-activite — récap par type (periodes[].lignes)
 * GET /api/statistiques/rapport-activite/ventes — ventes (nombre_de_tickets, ca_brut, …)
 */
export default {
  name: "RapportActivite",
  components: { SpinnerLoading },
  props: {
    filters: { type: Object, required: true },
    period: { type: String, required: true },
  },
  data() {
    return {
      isLoading: false,
      isExporting: false,
      fetchError: null,
      infoBanniere: null,
      periodeDu: "",
      periodeAu: "",
      ventes: {
        directes: emptyVenteCol(),
        credit: emptyVenteCol(),
        total: emptyVenteCol(),
        ticket_moyen: null,
      },
      reglements: { clients: null, divers: null },
      achats: {
        valides: { bl: emptyBloc(), avoirs: emptyBloc() },
        fournisseurs: { bl: emptyBloc(), avoirs: emptyBloc() },
      },
      echanges: {
        entree: { nb: null, ppv: null },
        sorties: { nb: null, ppv: null },
        solde: { nb: null, ppv: null },
      },
      recapLignes: [],
      recapTotaux: { prix_public_ttc: null, pp_bl_avoirs: null, pph_bl_avoirs: null },
      categories: [],
      recapLignesBase: [],
      recapPeriodKey: "",
      rapportReady: false,
      fetchSeq: 0,
    };
  },
  computed: {
    nomPharmacie() {
      const p = this.$store.state.pharmacie;
      return (p && p.nom_pharmacie) || "PHARMACIE";
    },
    logoSrc() {
      const p = this.$store.state.pharmacie;
      if (!p) return logoClementine;
      return p.logo_url || p.logo || logoClementine;
    },
    periodeDuLabel() {
      return this.formatFr(this.periodeDu) || "—";
    },
    periodeAuLabel() {
      return this.formatFr(this.periodeAu) || "—";
    },
    dateGenerationLabel() {
      const d = new Date();
      return this.formatFr(
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
      );
    },
    canExport() {
      return Boolean(this.rapportReady && this.hasDateFilters() && !this.isLoading);
    },
    exportFilename() {
      const f = this.filters || {};
      const from = String(f.period_from || f.debut || "debut").slice(0, 10);
      const to = String(f.period_to || f.fin || from).slice(0, 10);
      return `rapport-activite_${from}_${to}.pdf`;
    },
  },
  watch: {
    filters: { deep: true, handler() { this.tryFetchRapport(); } },
    period() { this.tryFetchRapport(); },
  },
  mounted() {
    this.tryFetchRapport();
  },
  methods: {
    updateChart() {
      this.tryFetchRapport();
    },
    hasDateFilters() {
      const f = this.filters || {};
      const du = f.period_from || f.debut;
      const au = f.period_to || f.fin;
      return Boolean(du && au);
    },
    tryFetchRapport() {
      if (!this.hasDateFilters()) {
        this.fetchError =
          "Sélectionnez une période (début et fin) puis cliquez sur Valider pour charger le rapport.";
        this.resetState();
        const { du, au } = this.inferPeriode();
        this.periodeDu = du;
        this.periodeAu = au || du;
        return;
      }
      this.fetchRapport();
    },
    pick(...values) {
      for (let i = 0; i < values.length; i += 1) {
        const v = values[i];
        if (v !== null && v !== undefined && v !== "") return v;
      }
      return null;
    },
    extractRapportRoot(response) {
      const body = response && response.data !== undefined ? response.data : response;
      if (!body || typeof body !== "object") return null;
      if (body.data !== undefined && body.data !== null && typeof body.data === "object") {
        return body.data;
      }
      return body;
    },
    formatFr(iso) {
      if (!iso || typeof iso !== "string") return "";
      if (iso.includes("/")) return iso.trim().slice(0, 10);
      const m = iso.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return "";
      return `${m[3]}/${m[2]}/${m[1]}`;
    },
    inferPeriode() {
      const f = this.filters || {};
      const du = f.period_from || f.debut || "";
      const au = f.period_to || f.fin || du || "";
      return { du: du.slice(0, 10), au: au.slice(0, 10) || du.slice(0, 10) };
    },
    fmtMoney(v) {
      if (v === null || v === undefined || v === "") return "";
      if (typeof this.$moneyFormat === "function") return this.$moneyFormat(v);
      return String(v);
    },
    fmtInt(v) {
      if (v === null || v === undefined || v === "") return "";
      return String(v);
    },
    fmtTaux(v) {
      if (v === null || v === undefined || v === "") return "";
      const n = Number(v);
      if (Number.isNaN(n)) return "";
      return n.toFixed(2);
    },
    fmtRecapTaux(v) {
      if (v === null || v === undefined || v === "") return "";
      const n = Number(v);
      if (Number.isNaN(n)) return "";
      return n.toFixed(4).replace(/\.?0+$/, "");
    },
    resetState() {
      this.ventes = {
        directes: emptyVenteCol(),
        credit: emptyVenteCol(),
        total: emptyVenteCol(),
        ticket_moyen: null,
      };
      this.reglements = { clients: null, divers: null };
      this.achats = {
        valides: { bl: emptyBloc(), avoirs: emptyBloc() },
        fournisseurs: { bl: emptyBloc(), avoirs: emptyBloc() },
      };
      this.echanges = {
        entree: { nb: null, ppv: null },
        sorties: { nb: null, ppv: null },
        solde: { nb: null, ppv: null },
      };
      this.recapLignes = [];
      this.recapLignesBase = [];
      this.recapTotaux = { prix_public_ttc: null, pp_bl_avoirs: null, pph_bl_avoirs: null };
      this.recapPeriodKey = "";
      this.rapportReady = false;
    },
    applyRecapParTypeFromRoot(root) {
      const lignesBrutes = extractLignesBrutes(root || {});
      this.recapLignesBase = lignesBrutesToRecapActivite(lignesBrutes);
      // Cette section affiche le récapitulatif par type du stock écoulé sur la période sélectionnée.
      // Les données sont extraites du rapport d'activité et non du rapport de stock en cours.
      this.recapTotaux = computeRecapActiviteTotaux(this.recapLignesBase);
      this.recapLignes = JSON.parse(JSON.stringify(this.recapLignesBase));
      // Ensure lines for all product categories are present (0 when no sales)
      this.ensureAllCategoriesPresent();
      const f = this.filters || {};
      this.recapPeriodKey = [
        normalizeTypePeriode(String(this.period || "")),
        String(f.period_from || "").slice(0, 10),
        String(f.period_to || "").slice(0, 10),
      ].join("|");
    },
    buildRapportUrl() {
      const url = `/api/statistiques/rapport-activite`;
      const params = new URLSearchParams();
      const f = this.filters || {};
      if (this.period) {
        params.append("type_periode", normalizeTypePeriode(String(this.period)));
      }
      const du = String(f.period_from || f.debut || "").slice(0, 10);
      const au = String(f.period_to || f.fin || "").slice(0, 10);
      if (du) params.append("period_from", du);
      if (au) params.append("period_to", au);
      const qs = params.toString();
      return qs ? `${url}?${qs}` : url;
    },
    buildRapportVentesUrl() {
      const url = `/api/statistiques/rapport-activite/ventes`;
      const params = new URLSearchParams();
      const f = this.filters || {};
      if (this.period) {
        params.append("type_periode", normalizeTypePeriode(String(this.period)));
      }
      const du = String(f.period_from || f.debut || "").slice(0, 10);
      const au = String(f.period_to || f.fin || du || "").slice(0, 10);
      if (du) params.append("period_from", du);
      if (au) params.append("period_to", au);
      const qs = params.toString();
      return qs ? `${url}?${qs}` : url;
    },
    mapVenteCol(src) {
      if (!src || typeof src !== "object") return emptyVenteCol();
      return {
        tickets: this.pick(
          src.nombre_de_tickets,
          src.nombre_tickets,
          src.tickets,
          src.nb_tickets,
          src.nombre
        ),
        ca_brut: this.pick(src.ca_brut, src.chiffre_affaire_brut, src.ca_brut_total, src.montant_brut),
        remise: this.pick(src.remise, src.montant_remise, src.total_remise),
        ca_net: this.pick(src.ca_net, src.chiffre_affaire_net, src.ca_net_total, src.montant_net),
      };
    },
    mapAchatsDoc(src) {
      if (!src || typeof src !== "object") return { nb: 0, ppv: null, pph: null };
      return {
        nb:
          this.pick(
            src.nombre_de_commandes,
            src.commandes,
            src.nb_commandes,
            src.nombre_de_documents,
            src.nombre_documents,
            src.nombre,
            src.nb,
            src.nombre_de_document
          ) || 0,
        ppv: this.pick(src.ppv, src.prix_public_vente, src.prix_public, src.prix_de_vente, src.montant_ppv),
        pph: this.pick(src.pph, src.prix_public_ht, src.ht, src.montant_pph),
      };
    },
    mapEchangeCol(src) {
      if (!src || typeof src !== "object") return { nb: null, ppv: null };
      return {
        nb: this.pick(src.nombre_d_echanges, src.nombre_echanges, src.nombre, src.nb),
        ppv: this.pick(src.ppv, src.prix_public_vente, src.prix_public, src.prix_de_vente),
      };
    },
    extractRecapLignes(root) {
      if (!root || typeof root !== "object") return [];
      const fromPeriodes = extractLignesBrutes(root);
      if (fromPeriodes.length) return fromPeriodes;
      const recap = root.recapitulatif_par_type ?? root.recap_par_type ?? root.recap;
      if (Array.isArray(recap)) return recap;
      if (recap && typeof recap === "object") {
        const lignes = recap.lignes ?? recap.rows ?? recap.lines;
        if (Array.isArray(lignes)) return lignes;
      }
      return [];
    },
    hasRapportData(root) {
      if (!root || typeof root !== "object") return false;
      return Boolean(
        root.ventes ||
        root.reglements ||
        root.achats ||
        root.echanges ||
        root.echanges_ppm ||
        (Array.isArray(root.periodes) && root.periodes.length) ||
        this.extractRecapLignes(root).length
      );
    },
    applyVentesRoot(root) {
      const { du, au } = this.inferPeriode();
      this.periodeDu = this.pick(root.period_from, root.periode_du, root.date_du, du);
      this.periodeAu = this.pick(root.period_to, root.periode_au, root.date_au, au);

      const v = root.ventes || {};
      this.ventes.directes = this.mapVenteCol(
        this.pick(v.ventes_directes_especes_cheques, v.directes_especes_cheques, v.directes, v.ventes_directes, v.direct)
      );
      this.ventes.credit = this.mapVenteCol(
        this.pick(
          v.ventes_a_credit_et_carte,
          v.ventes_credit_carte,
          v.credit_carte,
          v.credit,
          v.ventes_credit
        )
      );
      this.ventes.total = this.mapVenteCol(
        this.pick(v.total_ventes, v.total, v.totaux, v.totaux_ventes)
      );
      if (this.ventes.total.tickets === null) {
        this.ventes.total.tickets = this.pick(
          root.nombre_de_tickets,
          root.nombre_ventes_validees
        );
      }
      if (
        this.ventes.total.tickets === null &&
        (this.ventes.directes.tickets !== null || this.ventes.credit.tickets !== null)
      ) {
        this.ventes.total = {
          tickets:
            (Number(this.ventes.directes.tickets) || 0) + (Number(this.ventes.credit.tickets) || 0),
          ca_brut:
            (Number(this.ventes.directes.ca_brut) || 0) + (Number(this.ventes.credit.ca_brut) || 0),
          remise:
            (Number(this.ventes.directes.remise) || 0) + (Number(this.ventes.credit.remise) || 0),
          ca_net:
            (Number(this.ventes.directes.ca_net) || 0) + (Number(this.ventes.credit.ca_net) || 0),
        };
      }
      this.ventes.ticket_moyen = this.pick(
        v.ticket_moyen,
        v.ticket_moyen_total,
        root.ticket_moyen
      );
      if (
        (this.ventes.ticket_moyen === null || this.ventes.ticket_moyen === undefined) &&
        Number(this.ventes.total.tickets) > 0
      ) {
        this.ventes.ticket_moyen =
          (Number(this.ventes.total.ca_net) || 0) / Number(this.ventes.total.tickets);
      }
    },
    applyRoot(root) {
      const { du, au } = this.inferPeriode();
      this.periodeDu = this.pick(root.period_from, root.periode_du, root.date_du, du);
      this.periodeAu = this.pick(root.period_to, root.periode_au, root.date_au, au);

      this.applyVentesRoot(root);

      const r = root.reglements || {};
      this.reglements.clients = 0;
      this.reglements.divers = 0;

      /*
       * Dans le rapport d'activité, les règlements clients et divers doivent toujours être affichés à 0.
       * Les données sous-jacentes peuvent être présentes, mais l'affichage reste fixe.
       */

      const ach = root.achats || {};
      this.mergeAchatsBloc(
        this.achats.valides,
        ach.documents_valides || ach.valides || ach.validated
      );
      this.mergeAchatsBloc(
        this.achats.fournisseurs,
        ach.documents_dates_fournisseurs || ach.dates_fournisseurs || ach.fournisseurs
      );

      const e = root.echanges_ppm || root.echanges || root.exchanges || {};
      this.echanges.entree = this.mapEchangeCol(e.entree || e.entrees || e.in);
      this.echanges.sorties = this.mapEchangeCol(e.sorties || e.sortie || e.out);
      const soldeSrc = e.solde || e.balance;
      if (soldeSrc) {
        this.echanges.solde = this.mapEchangeCol(soldeSrc);
      } else {
        this.echanges.solde = {
          nb:
            (Number(this.echanges.entree.nb) || 0) - (Number(this.echanges.sorties.nb) || 0),
          ppv:
            (Number(this.echanges.entree.ppv) || 0) - (Number(this.echanges.sorties.ppv) || 0),
        };
      }

      // Récap par type : periodes[].lignes du rapport-activite
      this.applyRecapParTypeFromRoot(root);
    },
    mergeAchatsBloc(target, src) {
      if (!src) return;
      const bl = src.bons_de_livraison || src.bons_livraison || src.bl || {};
      const av = src.avoirs || {};
      target.bl = this.mapAchatsDoc(bl);
      target.avoirs = this.mapAchatsDoc(av);
    },

    ensureAllCategoriesPresent() {
      try {
        const categories = Array.isArray(this.categories) ? this.categories : [];
        const mapExist = new Map();
        this.recapLignes.forEach((row) => {
          const key = normalizeRecapType(row.type_actuel);
          mapExist.set(key, row);
        });

        categories.forEach((cat) => {
          const lib = cat.libelle || cat.nom || cat.label || "";
          const key = normalizeRecapType(lib);
          if (mapExist.has(key)) {
            mapExist.get(key).type_actuel = lib;
          } else {
            const taux = overrideMargeParType(lib, null);
            this.recapLignes.push({ type_actuel: lib, taux_marge: taux, prix_public_ttc: 0 });
          }
        });

        this.recapLignes.sort((a, b) => String(a.type_actuel).localeCompare(String(b.type_actuel), "fr"));
        // totaux remain unchanged (calculated from actual period data only)
      } catch (e) {
        console.error("ensureAllCategoriesPresent", e);
      }
    },
    async fetchRapport() {
      const fetchId = ++this.fetchSeq;
      this.isLoading = true;
      this.fetchError = null;
      this.infoBanniere = null;
      this.resetState();

      const { du, au } = this.inferPeriode();
      this.periodeDu = du;
      this.periodeAu = au || du;

      try {
        const [activiteResponse, ventesResponse, categoriesResponse] = await Promise.all([
          this.axios.get(this.buildRapportUrl()),
          this.axios.get(this.buildRapportVentesUrl()).catch(() => null),
          this.axios.get('/api/prod_categories').catch(() => null),
        ]);
        if (fetchId !== this.fetchSeq) return;

        const recapRoot = this.extractRapportRoot(activiteResponse) || {};
        const ventesRoot = ventesResponse ? this.extractRapportRoot(ventesResponse) : null;
        // Load categories list (prod_categories)
        this.categories = [];
        if (categoriesResponse && categoriesResponse.data) {
          this.categories = categoriesResponse.data.data || [];
        }

        this.applyRecapParTypeFromRoot(recapRoot);
        if (ventesRoot && (ventesRoot.ventes || ventesRoot.nombre_de_tickets != null)) {
          this.applyVentesRoot(ventesRoot);
        }

        const hasVentes =
          ventesRoot &&
          ventesRoot.ventes &&
          (Number(this.ventes.total.tickets) > 0 ||
            Number(this.ventes.directes.tickets) > 0 ||
            Number(this.ventes.credit.tickets) > 0 ||
            Number(this.ventes.total.ca_brut) > 0 ||
            Number(this.ventes.total.ca_net) > 0);

        if (hasVentes || this.recapLignes.length) {
          this.rapportReady = true;
          return;
        }

        this.fetchError = "Aucune donnée disponible pour cette période.";
      } catch (e) {
        if (fetchId !== this.fetchSeq) return;
        console.error("RapportActivite:", e);
        this.fetchError =
          "Impossible de charger le rapport d'activité.";
      } finally {
        if (fetchId === this.fetchSeq) {
          this.isLoading = false;
        }
      }
    },
    buildPdfFooter() {
      const p = this.$store.state.pharmacie;
      if (!p) return { line1: "", line2: "" };
      const parts1 = [p.nom_pharmacie, p.adresse, p.telephone1 ? `Tél: ${p.telephone1}` : ""].filter(Boolean);
      const parts2 = [p.rccm ? `RCCM: ${p.rccm}` : "", p.nif ? `NIF: ${p.nif}` : "", p.email || ""].filter(Boolean);
      return { line1: parts1.join(" | "), line2: parts2.join(" | ") };
    },
    async exportPdf() {
      if (!this.rapportReady || !this.hasDateFilters() || this.isExporting) return;
      const el = this.$refs.rapportExportRoot;
      if (!el) return;

      this.isExporting = true;
      try {
        await this.$nextTick();
        this.printElement(el, `Rapport d'activité ${this.exportFilename}`);
      } catch (e) {
        console.error("Impression rapport activité:", e);
      } finally {
        this.isExporting = false;
      }
    },

    printElement(el, title) {
      const printWindow = window.open("", "_blank");
      if (!printWindow) return;
      const styles = Array.from(document.querySelectorAll("style, link[rel='stylesheet']"))
        .map((node) => node.outerHTML)
        .join("\n");
      printWindow.document.write(`<!doctype html><html><head><title>${title}</title>${styles}</head><body>${el.outerHTML}</body></html>`);
      printWindow.document.close();
      printWindow.focus();
      printWindow.onload = () => {
        printWindow.print();
      };
    },
  },
};
</script>

<style scoped>
.rapport-activite-loading {
  position: absolute;
  inset: 0;
  z-index: 3;
  background: rgba(255, 255, 255, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
}

.rapport-export-root {
  background: #fff;
}

.rapport-page--dim {
  opacity: 0.55;
  pointer-events: none;
}

.rapport-page {
  background: #fff;
  color: #111;
  font-size: 12px;
  padding: 12px 14px;
}

.rapport-head {
  border-bottom: 1px solid #ccc;
  padding-bottom: 8px;
  margin-bottom: 4px;
}

.rapport-logo {
  max-height: 48px;
  max-width: 64px;
  object-fit: contain;
}

.rapport-logo-ph {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #e8f5e9;
  color: #2e7d32;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.rapport-pharma {
  font-size: 14px;
  letter-spacing: 0.03em;
}

.rapport-title-frame {
  border: 2px solid #333;
  padding: 10px 14px;
  text-align: center;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.03em;
  background: #b39ddb;
  color: #111;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  margin: 10px 0 6px;
}

.rapport-table thead th {
  font-size: 11px;
  vertical-align: middle;
}

.rapport-table-ventes thead th {
  background: #ede7f6 !important;
}

.rapport-table-achats thead th {
  background: #e3f2fd !important;
}

.rapport-table-echanges thead th {
  background: #fffde7 !important;
}

.rapport-table-recap thead th {
  background: #ececec !important;
}

.rapport-table td,
.rapport-table th {
  border-color: #999 !important;
  padding: 5px 8px;
  font-size: 11px;
}

.rapport-totaux-row td {
  background: #d9d9d9 !important;
  border-top: 2px solid #666 !important;
}

.rapport-ticket-moyen-row td {
  background: #d9d9d9 !important;
  border-top: 2px solid #666 !important;
}

.rapport-reglements div {
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 700;
}
</style>
